import { createFileRoute, Link } from "@tanstack/react-router";
import { abs } from "@/lib/site";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";

// Bands are built from the full symbol index — dynamic-import in the loader so
// the route module's static graph (bundled globally by routeTree) stays light.
// Return only label/count + an 8-glyph preview for each band.
export const Route = createFileRoute("/index/codepoints/")({
  loader: async () => {
    const { codepointBands } = await import("@/data/indexes");
    return {
      bands: codepointBands.map((b) => ({
        slug: b.slug,
        label: b.label,
        count: b.symbols.length,
        preview: b.symbols.slice(0, 8).map((s) => s.symbol),
      })),
    };
  },
  head: ({ loaderData }) => {
    const n = loaderData?.bands.length ?? 0;
    return {
      meta: [
        { title: `Codepoint range index — ${n} Unicode bands` },
        {
          name: "description",
          content: `Browse every indexed symbol by hexadecimal codepoint range. ${n} 256-codepoint bands covering the indexed slice of Unicode.`,
        },
        { property: "og:title", content: "Codepoint range index" },
        { property: "og:description", content: "256-codepoint bands across the indexed Unicode catalogue." },
        { property: "og:url", content: abs("/index/codepoints") },
      ],
      links: [{ rel: "canonical", href: abs("/index/codepoints") }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Codepoint range index",
            url: "/index/codepoints",
            numberOfItems: n,
          }),
        },
      ],
    };
  },
  component: CodepointsIndex,
});

function CodepointsIndex() {
  const { bands } = Route.useLoaderData();
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-12 sm:px-6 sm:py-16">
        <nav className="mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground sm:text-sm">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <span>/</span>
          <span className="text-foreground">Codepoint ranges</span>
        </nav>

        <h1 className="font-display text-4xl font-bold sm:text-5xl">
          Codepoint <span className="text-gradient">ranges</span>
        </h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          Every indexed character grouped into 256-codepoint bands aligned on
          <code className="ml-1 font-mono">0xNN00</code> boundaries — handy
          for queries like "U+2728 symbol" or "what lives near U+2600".
        </p>

        <div className="mt-4 text-sm">
          <Link to="/index/a-z" className="text-accent hover:underline">
            Or browse alphabetically →
          </Link>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {bands.map((b) => (
            <Link
              key={b.slug}
              to="/index/codepoints/$range"
              params={{ range: b.slug }}
              className="group glass rounded-2xl p-5 transition-all hover:-translate-y-0.5 hover:shadow-[var(--shadow-glow)]"
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-mono text-sm font-semibold">{b.label}</span>
                <span className="text-xs text-muted-foreground">{b.count}</span>
              </div>
              <p className="mt-3 text-2xl leading-snug" aria-hidden="true">
                {b.preview.join(" ")}
              </p>
            </Link>
          ))}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
